// src/context/CompetenciaContext.js

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { v4 as uuidv4 } from 'uuid';
import slugify from 'slugify';
import Papa from 'papaparse';
import predefinedPruebas from '../constants/predefinedPruebas';

// Importa useAuth para acceder al usuario desde AuthContext
import { useAuth } from './AuthContext'; // Asegúrate que la ruta sea correcta

// Importaciones de Firebase Firestore V9
import { db } from '../firebase'; // Solo necesitas db aquí ahora
import {
  collection,
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  query,
  where,
  orderBy,
  onSnapshot,
  writeBatch,
  increment,
  Timestamp,
  getDoc,
  arrayRemove, // Necesario para validaciones y operaciones detalladas
} from 'firebase/firestore';

const CompetenciaContext = createContext();

export const useCompetencia = () => useContext(CompetenciaContext);

export const CompetenciaProvider = ({ children }) => {
  // Cache liviano para inscripciones por competencia
  const [competencias, setCompetencias] = useState([]); // Caché local de competencias del usuario
  const [loading, setLoading] = useState(true); // Estado de carga inicial de competencias
  const { user } = useAuth(); // Obtener el usuario del AuthContext

  // --- Memos y Defaults ---

  const predefinedPruebasMap = useMemo(() => {
    return predefinedPruebas.reduce((map, prueba) => {
      map[prueba.eventCode] = prueba;
      return map;
    }, {});
  }, []);

  const defaultRegistroFields = useMemo(() => ({
    telefono: false, ciudad: false, estado: false, pais: false, direccion: false,
    fotoPerfil: false, contactoEmergencia: false, menorEdad: false, logoEquipo: false,
    asociacion: false, cuentaId: true,
  }), []);
  const defaultRegistroFieldLabels = useMemo(() => ({
    nombre: 'Nombre(s)', apellido: 'Apellido(s)', rama: 'Rama', fechaNacimiento: 'Fecha de Nacimiento',
    equipo: 'Equipo', categoria: 'Categoría', pruebas: 'Pruebas', email: 'Email', telefono: 'Teléfono',
    ciudad: 'Ciudad', estado: 'Estado', pais: 'País', direccion: 'Dirección', fotoPerfil: 'Foto de Perfil',
    contactoEmergencia: 'Contacto de Emergencia', menorEdad: 'Informacion del tutor(a) si es menor de Edad',
    logoEquipo: 'Logo del Equipo', asociacion: 'Asociación', cuentaId: 'Cuenta id',
  }), []);

  // ** NUEVA ESTRUCTURA POR DEFECTO ANIDADA **
  const defaultCompetenciaFirestoreFields = useMemo(() => ({
    // Metadatos Nivel Superior
    creatorUid: null,
    createdAt: null, // Se establecerán con Timestamp.now()
    updatedAt: null,
    slug: '',
    inscritos: 0,

    // Secciones Anidadas
    general: {
        nombre: '',
        subtitulo: '',
        fechaInicioCompetencia: '',
        fechaFinCompetencia: '',
        fechaInicioInscripciones: '',
        fechaFinInscripciones: '',
        tipoCompetencia: '',
        estado: 'pendiente',
        precioInscripciones: 0,
        imagenPortada: '', // URL
        logo: '', // URL
    },
    // Sección separada para convocatoria
    convocatoria: {
      convocatoria: '',     // Texto o descripción
      convocatoriaPDF: '', // URL al PDF en Storage
      displayPreference: 'auto', // NUEVO CAMPO: 'auto', 'text', 'pdf'
    },
    location: {
        lugar: '',
        direccion: '',
        mapShortLink: '',
        mapEmbedLink: '',
    },
    contact: {
        contactos: [], // Array vacío por defecto [{nombre, email, telefono}]
    },
    social: {
        facebook: '',
        instagram: '',
        twitter: '',
        whatsapp: '',
        sitioWeb: '',
    },
    theme: {
        colorPrimario: '#1976d2',
        colorSecundario: '#dc004e',
    },
    rules: {
        maxPruebasIndPorRegistro: 3,
        maxRelevosPorRegistro: 1,
        habilitarMarcasSiembra: false,
        requerirMiembrosRelevo: false,
        opcionesRelevos: {
            minMiembros: 0, maxMiembros: 4, permitirEquiposMixtos: false,
            permitirSuplentes: false, numeroSuplentes: 0,
        },
    },
    registration: {
        config: {
            registroFields: { ...defaultRegistroFields }, // Copiar defaults
            registroFieldLabels: { ...defaultRegistroFieldLabels }, // Copiar defaults
        },
    },
}), [defaultRegistroFields, defaultRegistroFieldLabels]); // Mantener dependencias

  // --- Generadores y Helpers ---
  const generateUniqueSlug = useCallback((nombre, competenciasList) => {
    let slugBase = slugify(nombre, { lower: true, strict: true });
    let slug = slugBase;
    let counter = 1;
    while (competenciasList.some((comp) => comp.slug === slug)) {
        slug = `${slugBase}-${counter}`;
        counter++;
    }
    return slug;
  }, []);

  const getDefaultFechaCorte = useCallback(() => {
    const currentYear = new Date().getFullYear();
    const fecha = new Date(currentYear, 11, 31);
    return fecha.toISOString().split('T')[0];
  }, []);

  // --- EFECTOS ---
  useEffect(() => {
    const fetchCompetencias = async (userId) => {
      console.log(`[CompetenciaContext] Cargando competencias para UID: ${userId}`);
      setLoading(true);
      try {
        const q = query(collection(db, "competencias"), where("creatorUid", "==", userId));
        const querySnapshot = await getDocs(q);
        const compsFromFirestore = querySnapshot.docs.map(docSnap => {
            // Aplicar defaults aquí para asegurar que la estructura anidada exista
            const data = docSnap.data();
            // Fusionar con defaults profundamente
            const mergeDeep = (target, source) => {
                const output = { ...target };
                if (isObject(target) && isObject(source)) {
                    Object.keys(source).forEach(key => {
                        if (isObject(source[key])) {
                            if (!(key in target)) Object.assign(output, { [key]: source[key] });
                            else output[key] = mergeDeep(target[key], source[key]);
                        } else {
                            Object.assign(output, { [key]: source[key] });
                        }
                    });
                }
                return output;
            };
            const isObject = (item) => {
                 return (item && typeof item === 'object' && !Array.isArray(item));
            };

            const mergedData = mergeDeep(defaultCompetenciaFirestoreFields, data);

            return {
                ...mergedData,
                id: docSnap.id, // Añadir ID
                // Inicializar arrays locales vacíos si no existen en Firestore (o si no quieres cargarlos siempre)
                categorias: [],
                pruebas: [],
                inscripciones: [], // Generalmente no se cargan aquí por volumen
                sessions: [],
            };
        });
        setCompetencias(compsFromFirestore);
        console.log(`[CompetenciaContext] ${compsFromFirestore.length} competencias cargadas.`);
      } catch (error) {
        console.error(`[CompetenciaContext] Error cargando competencias: `, error);
        setCompetencias([]);
      } finally {
        setLoading(false);
      }
    };
    if (user?.uid) { fetchCompetencias(user.uid); }
    else { setCompetencias([]); setLoading(false); }
  }, [user?.uid, defaultCompetenciaFirestoreFields]); // Ejecutar si cambia el usuario O los defaults

  // Guardar en localStorage (opcional, puede eliminarse si no se necesita)
  useEffect(() => {
    if (!loading && user !== undefined) {
        try {
            // Considerar no guardar subcolecciones grandes en LS
            const competenciasToSave = competencias.map(({ categorias, pruebas, inscripciones, sessions, ...rest }) => rest);
            localStorage.setItem('competencias', JSON.stringify(competenciasToSave));
        } catch (error) {
            console.error("[CompetenciaContext] Error guardando en LS: ", error);
        }
    }
  }, [competencias, loading, user]);

  // --- FUNCIONES CRUD PRINCIPALES ---

  // **ADD**
  const addCompetencia = useCallback(async (competenciaData, userId, newCompetenciaId) => {
    if (!userId || !newCompetenciaId) return { success: false, error: new Error("Faltan IDs requeridos") };

    const slug = generateUniqueSlug(competenciaData.nombre, competencias);
    const now = Timestamp.now();

    // Construir documento con estructura anidada usando defaults como base
    const nuevaCompetenciaDoc = {
        ...defaultCompetenciaFirestoreFields, // Empezar con todos los defaults
        creatorUid: userId,
        createdAt: now,
        updatedAt: now,
        slug: slug,
        inscritos: 0, // Siempre inicia en 0
        // Sobrescribir con datos iniciales proporcionados
        general: {
            ...defaultCompetenciaFirestoreFields.general,
            nombre: competenciaData.nombre || '',
            subtitulo: competenciaData.subtitulo || '',
            fechaInicioCompetencia: competenciaData.fechaInicioCompetencia || '',
            fechaFinCompetencia: competenciaData.fechaFinCompetencia || '',
            fechaInicioInscripciones: competenciaData.fechaInicioInscripciones || '',
            fechaFinInscripciones: competenciaData.fechaFinInscripciones || '',
            tipoCompetencia: competenciaData.tipoCompetencia || '',
            estado: competenciaData.estado || 'pendiente',
            precioInscripciones: competenciaData.precioInscripciones || 0,
            imagenPortada: competenciaData.imagenPortada || '',
            logo: competenciaData.logo || '',
        },
        location: {
            ...defaultCompetenciaFirestoreFields.location,
            lugar: competenciaData.lugar || '',
            // Puedes añadir más campos iniciales si los recoges en el primer formulario
        },
        // Otras secciones usarán los defaults si no se proporcionan datos iniciales
        // Ejemplo: contact, social, theme, rules, registration, convocatoria
         contact: {
            ...defaultCompetenciaFirestoreFields.contact,
            contactos: Array.isArray(competenciaData.contactos) ? competenciaData.contactos : defaultCompetenciaFirestoreFields.contact.contactos,
        },
        // Añadir más secciones si recoges datos iniciales para ellas
    };

    console.log("[CompetenciaContext] Guardando competencia:", newCompetenciaId, nuevaCompetenciaDoc);
    try {
        await setDoc(doc(db, "competencias", newCompetenciaId), nuevaCompetenciaDoc);
        // Añadir al estado local con estructura completa (incluyendo arrays vacíos)
        const newLocalCompetencia = {
            ...nuevaCompetenciaDoc, // Datos guardados en Firestore
            id: newCompetenciaId,
            categorias: [], // Inicializar arrays locales
            pruebas: [],
            inscripciones: [],
            sessions: [],
        };
        setCompetencias((prev) => [...prev, newLocalCompetencia]);
        console.log("[CompetenciaContext] Competencia añadida:", newCompetenciaId);
        return { success: true, id: newCompetenciaId };
    } catch (error) {
        console.error("[CompetenciaContext] Error añadiendo competencia:", error);
        return { success: false, error: error };
    }
}, [competencias, defaultCompetenciaFirestoreFields, generateUniqueSlug]);


  // **UPDATE (Base)** - Función interna para actualizar el documento principal
  const _updateCompetenciaDoc = useCallback(async (competenciaId, updatePayload) => {
    if (!competenciaId) return { success: false, error: new Error("ID de competencia no proporcionado") };
    if (typeof updatePayload !== 'object' || updatePayload === null || Object.keys(updatePayload).length === 0) {
        return { success: false, error: new Error("Payload de actualización inválido o vacío") };
    }

    const competenciaRef = doc(db, "competencias", competenciaId);
    // Asegurar que siempre incluya updatedAt
    const finalPayload = { ...updatePayload, updatedAt: Timestamp.now() };

    console.log(`[CompetenciaContext] Actualizando doc ${competenciaId} con:`, finalPayload);
    try {
      await updateDoc(competenciaRef, finalPayload);

      // Actualizar caché local (manejo de campos anidados con notación de punto o mapas completos)
      setCompetencias((prev) =>
        prev.map((comp) => {
          if (comp.id === competenciaId) {
            // Crear una copia profunda para evitar mutaciones no deseadas
            let updatedComp = JSON.parse(JSON.stringify(comp));

            // Aplicar las actualizaciones del payload
            for (const key in finalPayload) {
              if (key === 'updatedAt' && finalPayload.updatedAt instanceof Timestamp) {
                  // Convertir Timestamp a algo serializable si es necesario para el estado local (ej. ISO string)
                  // O simplemente asignarlo si tu estado puede manejar Timestamps (generalmente no recomendado directamente)
                  updatedComp[key] = finalPayload[key].toDate().toISOString(); // Ejemplo: convertir a ISO String
              } else if (key.includes('.')) {
                // Manejar notación de punto para campos anidados (ej. 'general.nombre')
                const keys = key.split('.');
                let currentLevel = updatedComp;
                for (let i = 0; i < keys.length - 1; i++) {
                  // Asegurar que los niveles intermedios existan y sean objetos
                  if (!currentLevel[keys[i]] || typeof currentLevel[keys[i]] !== 'object') {
                    currentLevel[keys[i]] = {};
                  }
                  currentLevel = currentLevel[keys[i]];
                }
                // Asignar el valor final en el nivel más profundo
                if(typeof currentLevel === 'object' && currentLevel !== null) {
                    currentLevel[keys[keys.length - 1]] = finalPayload[key];
                } else {
                     console.warn(`[CompetenciaContext] No se pudo actualizar estado local para ${key}, nivel intermedio no es objeto.`);
                }
              } else if (typeof finalPayload[key] === 'object' && finalPayload[key] !== null && !Array.isArray(finalPayload[key]) && !(finalPayload[key] instanceof Timestamp)) {
                 // Si el valor es un objeto (y no un array o Timestamp), fusionarlo recursivamente
                 // Esto maneja casos como payload = { general: { nombre: 'nuevo nombre' } }
                 const mergeDeep = (target, source) => {
                    let output = { ...target };
                    if (isObject(target) && isObject(source)) {
                        Object.keys(source).forEach(key => {
                        if (isObject(source[key])) {
                            if (!(key in target)) Object.assign(output, { [key]: source[key] });
                            else output[key] = mergeDeep(target[key], source[key]);
                        } else {
                            Object.assign(output, { [key]: source[key] });
                        }
                        });
                    }
                    return output;
                 };
                 const isObject = (item) => (item && typeof item === 'object' && !Array.isArray(item));
                 updatedComp[key] = mergeDeep(updatedComp[key] || {}, finalPayload[key]);
              }
              else {
                // Actualización de campo de nivel superior o array completo
                updatedComp[key] = finalPayload[key];
              }
            }
             // Asegurar que updatedAt se actualice correctamente en el estado local también
             if (finalPayload.updatedAt) {
                 updatedComp.updatedAt = finalPayload.updatedAt.toDate().toISOString(); // o como prefieras guardarlo
             }
            return updatedComp;
          }
          return comp;
        })
      );
      console.log("[CompetenciaContext] Competencia actualizada en Firestore y caché local:", competenciaId);
      return { success: true };
    } catch (error) {
      console.error("[CompetenciaContext] Error actualizando competencia:", competenciaId, error);
       return { success: false, error: error };
    }
  }, []); // Vacío, ya que setCompetencias no necesita ser dependencia directa


  const setCompetencia = useCallback(async (competenciaData) => {
    if (!competenciaData || !competenciaData.id) {
      return { success: false, error: new Error("Datos de competencia inválidos o falta ID") };
    }
  
    const competenciaId = competenciaData.id;
    
    // Crear una copia sin el ID para la actualización
    const updateData = { ...competenciaData };
    delete updateData.id;
  
    console.log(`[CompetenciaContext] setCompetencia llamada para ${competenciaId} con:`, updateData);
  
    try {
      // Usar la función base _updateCompetenciaDoc para actualizar
      const result = await _updateCompetenciaDoc(competenciaId, updateData);
      
      if (result.success) {
        console.log(`[CompetenciaContext] setCompetencia exitosa para ${competenciaId}`);
        return { success: true };
      } else {
        console.error(`[CompetenciaContext] setCompetencia falló para ${competenciaId}:`, result.error);
        return { success: false, error: result.error };
      }
    } catch (error) {
      console.error(`[CompetenciaContext] Error en setCompetencia para ${competenciaId}:`, error);
      return { success: false, error: error };
    }
  }, [_updateCompetenciaDoc]);
  
  // **DELETE**
  const deleteCompetencia = useCallback(async (id) => {
    if (!id) return { success: false, error: new Error("ID no proporcionado") };
    const competenciaRef = doc(db, "competencias", id);
    // Advertencia: Esto NO elimina subcolecciones automáticamente en Firestore.
    // Necesitarías una Cloud Function o eliminarlas manualmente/programáticamente por separado si es necesario.
    console.warn(`[CompetenciaContext] Borrando doc principal ${id}. Las subcolecciones (categorias, pruebas, etc.) NO se borrarán automáticamente en Firestore.`);
    try {
        await deleteDoc(competenciaRef);
        // Eliminar del caché local
        setCompetencias((prev) => prev.filter((comp) => comp.id !== id));
        console.log(`[CompetenciaContext] Documento principal ${id} eliminado de Firestore y caché local.`);
        return { success: true };
    } catch (error) {
        console.error("[CompetenciaContext] Error eliminando competencia:", id, error);
        return { success: false, error: error };
    }
  }, []);

  // --- FUNCIONES DE ACTUALIZACIÓN ESPECÍFICAS POR SECCIÓN ---
  // Cada una prepara el payload y llama a _updateCompetenciaDoc

  const updateGeneralInfo = useCallback(async (competenciaId, generalData) => {
    // Payload es el objeto 'general' completo
    const payload = { general: generalData };
    return _updateCompetenciaDoc(competenciaId, payload);
  }, [_updateCompetenciaDoc]);

  const updateImagenPortada = useCallback(async (competenciaId, imageUrl) => {
     // Payload usa notación de punto para actualizar un campo específico dentro de 'general'
     const payload = { 'general.imagenPortada': imageUrl ?? '' };
     return _updateCompetenciaDoc(competenciaId, payload);
  }, [_updateCompetenciaDoc]);

  const updateLogo = useCallback(async (competenciaId, logoUrl) => {
    const payload = { 'general.logo': logoUrl ?? '' };
    return _updateCompetenciaDoc(competenciaId, payload);
  }, [_updateCompetenciaDoc]);

  // **NUEVA** función para Convocatoria
  const updateConvocatoria = useCallback(async (competenciaId, convocatoriaData) => {
    console.log(`[CompetenciaContext] Recibido para actualizar convocatoria ${competenciaId}:`, convocatoriaData);
    if (!convocatoriaData || typeof convocatoriaData !== 'object') {
         console.error("[CompetenciaContext] updateConvocatoria recibió datos inválidos:", convocatoriaData);
         return { success: false, error: new Error("Datos de convocatoria inválidos") };
    }
    
    // Payload es el objeto 'convocatoria' completo
    // Asegurarse de que los campos existan, incluso si están vacíos
    const payload = {
        convocatoria: {
            convocatoria: convocatoriaData.convocatoria ?? '',
            convocatoriaPDF: convocatoriaData.convocatoriaPDF ?? '',
            displayPreference: convocatoriaData.displayPreference ?? 'auto', // NUEVO CAMPO
        }
    };
    return _updateCompetenciaDoc(competenciaId, payload);
}, [_updateCompetenciaDoc]);


  const updateLocationSettings = useCallback(async (competenciaId, locationData) => {
    const payload = { location: locationData };
    return _updateCompetenciaDoc(competenciaId, payload);
  }, [_updateCompetenciaDoc]);

  const updateContactInfo = useCallback(async (competenciaId, contactosArray) => {
    if (!Array.isArray(contactosArray)) {
        console.error("[CompetenciaContext] updateContactInfo esperaba un array:", contactosArray);
        return { success: false, error: new Error('Se esperaba un array para contactos') };
    }
    // Usa notación de punto si solo actualizas el array dentro de 'contact'
    const payload = { 'contact.contactos': contactosArray };
    return _updateCompetenciaDoc(competenciaId, payload);
  }, [_updateCompetenciaDoc]);

  const updateSocialMedia = useCallback(async (competenciaId, socialData) => {
    const payload = { social: socialData };
    return _updateCompetenciaDoc(competenciaId, payload);
  }, [_updateCompetenciaDoc]);

  const updateTheme = useCallback(async (competenciaId, themeData) => {
    const payload = { theme: themeData };
    return _updateCompetenciaDoc(competenciaId, payload);
  }, [_updateCompetenciaDoc]);

  // *** VERSIÓN AJUSTADA (MÁS ROBUSTA) de updateAdvancedRules ***
  const updateAdvancedRules = useCallback(async (competenciaId, opcionesAvanzadas) => {
    const payload = {}; 
    if (opcionesAvanzadas.hasOwnProperty('maxPruebasIndPorRegistro')) {
        payload['rules.maxPruebasIndPorRegistro'] = opcionesAvanzadas.maxPruebasIndPorRegistro ?? defaultCompetenciaFirestoreFields.rules.maxPruebasIndPorRegistro;
    }
    if (opcionesAvanzadas.hasOwnProperty('maxRelevosPorRegistro')) {
        payload['rules.maxRelevosPorRegistro'] = opcionesAvanzadas.maxRelevosPorRegistro ?? defaultCompetenciaFirestoreFields.rules.maxRelevosPorRegistro;
    }
    if (opcionesAvanzadas.hasOwnProperty('habilitarMarcasSiembra')) {
        // Para booleanos, '??' funciona bien. false ?? false es false, true ?? false es true.
        payload['rules.habilitarMarcasSiembra'] = opcionesAvanzadas.habilitarMarcasSiembra ?? defaultCompetenciaFirestoreFields.rules.habilitarMarcasSiembra;
    }
    if (opcionesAvanzadas.hasOwnProperty('requerirMiembrosRelevo')) {
        payload['rules.requerirMiembrosRelevo'] = opcionesAvanzadas.requerirMiembrosRelevo ?? defaultCompetenciaFirestoreFields.rules.requerirMiembrosRelevo;
    }
    // Solo llamar a la actualización si hay algo en el payload
    if (Object.keys(payload).length > 0) {
        console.log("[CompetenciaContext] Enviando actualización de reglas avanzadas:", payload);
        return _updateCompetenciaDoc(competenciaId, payload);
    } else {
        console.warn("[CompetenciaContext] updateAdvancedRules llamado sin cambios detectados en el payload.");
        // Devolver éxito porque no había nada que hacer, o un estado diferente si prefieres
        return { success: true, message: "No changes detected" };
    }
  }, [_updateCompetenciaDoc, defaultCompetenciaFirestoreFields.rules]); // Dependencias


  const updateRelayOptions = useCallback(async (competenciaId, opcionesRelevos) => {
     // Reemplaza el objeto completo 'opcionesRelevos' dentro de 'rules'
     // Asegura que todos los subcampos tengan valores (usando defaults si no vienen)
     const payload = {
         'rules.opcionesRelevos': {
             minMiembros: opcionesRelevos?.minMiembros ?? defaultCompetenciaFirestoreFields.rules.opcionesRelevos.minMiembros,
             maxMiembros: opcionesRelevos?.maxMiembros ?? defaultCompetenciaFirestoreFields.rules.opcionesRelevos.maxMiembros,
             permitirEquiposMixtos: opcionesRelevos?.permitirEquiposMixtos ?? defaultCompetenciaFirestoreFields.rules.opcionesRelevos.permitirEquiposMixtos,
             permitirSuplentes: opcionesRelevos?.permitirSuplentes ?? defaultCompetenciaFirestoreFields.rules.opcionesRelevos.permitirSuplentes,
             numeroSuplentes: opcionesRelevos?.numeroSuplentes ?? defaultCompetenciaFirestoreFields.rules.opcionesRelevos.numeroSuplentes,
         }
     };
     return _updateCompetenciaDoc(competenciaId, payload);
  }, [_updateCompetenciaDoc, defaultCompetenciaFirestoreFields.rules.opcionesRelevos]);


  const updateRegistrationConfig = useCallback(async (competenciaId, registrationConfigData) => {
      // registrationConfigData debe ser { registroFields: {...}, registroFieldLabels: {...} }
      // Reemplaza el objeto 'config' completo dentro de 'registration'
      const payload = {
          'registration.config': {
              registroFields: {
                  ...defaultCompetenciaFirestoreFields.registration.config.registroFields, // Asegura todos los campos base
                  ...(registrationConfigData?.registroFields || {}) // Sobrescribe con los datos recibidos
              },
              registroFieldLabels: {
                  ...defaultCompetenciaFirestoreFields.registration.config.registroFieldLabels,
                   ...(registrationConfigData?.registroFieldLabels || {})
              }
          }
      };
      return _updateCompetenciaDoc(competenciaId, payload);
  }, [_updateCompetenciaDoc, defaultCompetenciaFirestoreFields.registration.config]);


  // --- CRUD para Subcolecciones (Categorías, Pruebas, Inscripciones, Sessions) ---
  // Interactúan directamente con Firestore. La actualización del caché local es OPCIONAL.
  // Si decides actualizar el caché local, asegúrate de manejar correctamente la estructura.

// **Categorías**
const addCategoria = useCallback(async (competenciaId, categoria) => {
  console.log('[DEBUG] Iniciando addCategoria con:', { competenciaId, categoria });
  
  if (!competenciaId) {
      console.error("No se proporcionó competenciaId");
      return { wasAdded: false, reason: "Falta competenciaId" };
  }
  
  const categoriasColRef = collection(db, "competencias", competenciaId, "categorias");
  const newCatId = uuidv4();
  let assignedCatNumber = categoria.catNumber ? Number(categoria.catNumber) : null;
  let validationError = null;

  try {
      // Validación robusta contra Firestore
      const qCodigo = query(categoriasColRef, where("codigo", "==", categoria.codigo));
      const codigoSnapshot = await getDocs(qCodigo);
      const qCatNum = assignedCatNumber ? query(categoriasColRef, where("catNumber", "==", assignedCatNumber)) : null;
      
      if (!codigoSnapshot.empty) {
          return { 
              wasAdded: false, 
              reason: `El código '${categoria.codigo}' ya existe`,
              field: 'codigo'
          };
      }

      // Validar nombre único
      const qNombre = query(categoriasColRef, where("nombre", "==", categoria.nombre));
      const nombreSnapshot = await getDocs(qNombre);
      
      if (!nombreSnapshot.empty) {
          return { 
              wasAdded: false, 
              reason: `El nombre '${categoria.nombre}' ya existe`,
              field: 'nombre'
          };
      }

      const [catNumSnapshot, allCatsSnapshot] = await Promise.all([
          qCatNum ? getDocs(qCatNum) : Promise.resolve({ empty: true }),
          getDocs(categoriasColRef)
      ]);

      const existingCats = allCatsSnapshot.docs.map(doc => doc.data());

      if (!catNumSnapshot.empty) {
          validationError = `El número de categoría #${assignedCatNumber} ya existe en esta competencia.`;
      }

      if (validationError) {
          console.error("[CompetenciaContext] addCategoria validación fallida:", validationError);
          return { wasAdded: false, reason: validationError };
      }

      // Calcular siguiente #Cat si no se proporcionó
      if (!assignedCatNumber) {
          const maxExistingNum = existingCats.reduce((max, cat) => {
              const currentNum = cat.catNumber || 0;
              return currentNum > max ? currentNum : max;
          }, 0);
          assignedCatNumber = maxExistingNum + 1;
      } else {
          // Validar que el número asignado no exista ya
          const numberExists = existingCats.some(cat => cat.catNumber === assignedCatNumber);
          if (numberExists) {
              return { 
                  wasAdded: false, 
                  reason: `El número de categoría #${assignedCatNumber} ya existe`,
                  field: 'catNumber'
              };
          }
      }

      const nuevaCategoriaData = {
          ...categoria,
          id: newCatId,
          catNumber: assignedCatNumber,
          fechaCorte: categoria.fechaCorte || getDefaultFechaCorte(),
          nombre: categoria.nombre || '',
          edadMin: categoria.edadMin || 0,
          edadMax: categoria.edadMax || 0,
          codigo: categoria.codigo || '',
      };

      console.log('Guardando categoría en Firestore:', nuevaCategoriaData);

      await setDoc(doc(categoriasColRef, newCatId), nuevaCategoriaData);

      // Actualizar estado local de forma más robusta
      setCompetencias(prev => prev.map(comp => {
          if (comp.id === competenciaId) {
              return {
                  ...comp,
                  categorias: [...(comp.categorias || []), { ...nuevaCategoriaData, id: newCatId }]

              };
          }
          return comp;
      }));

      return { 
          wasAdded: true, 
          id: newCatId, 
          categoria: nuevaCategoriaData 
      };
  } catch (error) {
    console.error("Error detallado:", error);
    return { 
      wasAdded: false,
      reason: error.message || 'Error de servidor al guardar'
    }
}
}, [getDefaultFechaCorte]);

  const fetchCategorias = async (competenciaId) => {
    if (!competenciaId) return [];
    
    try {
      const categoriasColRef = collection(db, "competencias", competenciaId, "categorias");
      const snapshot = await getDocs(categoriasColRef);
      
      // Obtener la fecha de fin de competencia si es necesario
      const competenciaDoc = await getDoc(doc(db, "competencias", competenciaId));
      const fechaFinCompetencia = competenciaDoc.exists() 
        ? competenciaDoc.data().general?.fechaFinCompetencia 
        : null;
  
      return snapshot.docs.map(doc => ({ 
        id: doc.id, 
        ...doc.data(),
        nombre: doc.data().nombre || '',
        codigo: doc.data().codigo || '',
        edadMin: doc.data().edadMin || 0,
        edadMax: doc.data().edadMax || 0,
        fechaCorte: doc.data().fechaCorte || fechaFinCompetencia || getDefaultFechaCorte(),
        catNumber: doc.data().catNumber || null
      }));
    } catch (error) {
      console.error("Error fetching categorias:", error);
      return [];
    }
  };
  

  const addCategorias = useCallback(async (competenciaId, categoriasToAdd) => {
    // ... (sin cambios significativos respecto a tu versión anterior, valida y usa batch) ...
    if (!competenciaId) return { wasAdded: false, reason: 'Falta competenciaId' };
       const batch = writeBatch(db);
       const categoriasColRef = collection(db, "competencias", competenciaId, "categorias");
       const nuevasCategoriasParaEstadoLocal = [];
       let hasErrors = false;
       let errors = []; // Para almacenar errores detallados

       try {
           // Obtener datos actuales de Firestore para validación en memoria
           const currentCatsSnapshot = await getDocs(categoriasColRef);
           const existingData = currentCatsSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));
           const existingCodigos = new Set(existingData.map(cat => cat.codigo));
           const existingCatNumbers = new Set(existingData.map(cat => cat.catNumber).filter(n => n)); // Ignorar null/undefined/0
           let nextCatNumber = existingData.reduce((max, cat) => Math.max(max, cat.catNumber || 0), 0) + 1;

           // Sets para validar duplicados dentro del archivo CSV importado
           const importedCodigos = new Set();
           const importedCatNumbers = new Set();

           for (const [index, categoria] of categoriasToAdd.entries()) {
               let currentCatNum = categoria['#Cat'] ? Number(categoria['#Cat']) : NaN; // Ajustar al header del CSV si es '#Cat'
               const codigo = categoria.codigo?.trim();
               const nombre = categoria.nombre?.trim();
               const edadMin = categoria.edadMin ? Number(categoria.edadMin) : 0;
               const edadMax = categoria.edadMax ? Number(categoria.edadMax) : 0;
               const fechaCorte = categoria.fechaCorte?.trim() || getDefaultFechaCorte();
               const rowIndex = index + 2; // Asumiendo cabecera en fila 1
               let rowErrors = [];

               // Validar #Cat
               if (!isNaN(currentCatNum) && currentCatNum > 0) {
                   if (existingCatNumbers.has(currentCatNum) || importedCatNumbers.has(currentCatNum)) {
                       rowErrors.push(`El #Cat ${currentCatNum} está duplicado (existente o en el archivo).`);
                   }
               } else {
                   // Asignar siguiente número disponible, validando colisión al asignar
                   currentCatNum = nextCatNumber;
                   while(existingCatNumbers.has(currentCatNum) || importedCatNumbers.has(currentCatNum)) {
                        currentCatNum++; // Sigue buscando hasta encontrar uno libre
                   }
                   nextCatNumber = currentCatNum + 1; // Actualiza el próximo a probar
                   // No debería haber error aquí si la lógica de `while` es correcta
               }

               // Validar Código
               if (!codigo) {
                   rowErrors.push("El campo 'codigo' es obligatorio y no puede estar vacío.");
               } else if (existingCodigos.has(codigo) || importedCodigos.has(codigo)) {
                   rowErrors.push(`El código '${codigo}' está duplicado (existente o en el archivo).`);
               }

               // Validar Nombre
                if (!nombre) {
                   rowErrors.push("El campo 'nombre' es obligatorio.");
               }

               // Validar Edades (opcional, pero bueno tenerlo)
               if (isNaN(edadMin) || edadMin < 0) rowErrors.push("Valor inválido para 'edadMin'.");
               if (isNaN(edadMax) || edadMax < edadMin) rowErrors.push("Valor inválido para 'edadMax' (debe ser >= edadMin).");

               // Validar Fecha Corte (formato básico)
               if (!/^\d{4}-\d{2}-\d{2}$/.test(fechaCorte)) {
                   rowErrors.push("Formato inválido para 'fechaCorte' (debe ser YYYY-MM-DD).");
               }


               if (rowErrors.length > 0) {
                   hasErrors = true;
                   errors.push({ fila: rowIndex, categoria, errores: rowErrors });
                   // Podrías decidir continuar y solo reportar errores, o parar aquí (`break;`)
                   continue; // Saltar esta categoría y seguir validando las demás
               }

               // Si es válido, añadir al batch y a los sets de validación
               const newCatId = uuidv4();
               const nuevaCategoriaData = {
                   id: newCatId,
                   catNumber: currentCatNum,
                   codigo: codigo,
                   nombre: nombre,
                   edadMin: edadMin,
                   edadMax: edadMax,
                   fechaCorte: fechaCorte,
               };
               batch.set(doc(categoriasColRef, newCatId), nuevaCategoriaData);
               nuevasCategoriasParaEstadoLocal.push(nuevaCategoriaData); // Guardar para estado local
               importedCodigos.add(codigo);
               importedCatNumbers.add(currentCatNum);
               // Simular adición a existentes para validación intra-lote (si no se paró en error)
               existingCodigos.add(codigo);
               existingCatNumbers.add(currentCatNum);
           } // Fin del bucle for

           if (hasErrors) {
               console.error("[CompetenciaContext] Errores encontrados durante la validación del lote de categorías:", errors);
               // Devolver los errores detallados
               return { wasAdded: false, reason: 'Errores de validación en el archivo.', errors: errors, count: 0 };
           }

           // Si no hubo errores, ejecutar el batch
           await batch.commit();
           console.log(`[CompetenciaContext] ${nuevasCategoriasParaEstadoLocal.length} categorías añadidas en lote a ${competenciaId}`);

           // Opcional: Actualizar caché local
           setCompetencias(prev => prev.map(comp => {
               if (comp.id === competenciaId) {
                   // Asegurar que comp.categorias sea un array antes de concatenar
                   const currentCategories = Array.isArray(comp.categorias) ? comp.categorias : [];
                   return { ...comp, categorias: [...currentCategories, ...nuevasCategoriasParaEstadoLocal] };
               }
               return comp;
           }));
           return { wasAdded: true, reason: '', count: nuevasCategoriasParaEstadoLocal.length, errors: [] };

       } catch (error) {
           console.error("[CompetenciaContext] Error añadiendo lote de categorías:", competenciaId, error);
           return { wasAdded: false, reason: 'Error de Firestore durante el proceso de lote.', error: error, count: 0, errors: [] };
       }
  }, [getDefaultFechaCorte]); // Dependencia de getDefaultFechaCorte

  const updateCategoria = useCallback(async (competenciaId, categoriaId, newData) => {
    // ... (sin cambios significativos, validar duplicados si cambian código o #Cat) ...
    if (!competenciaId || !categoriaId) return { wasUpdated: false, reason: 'Faltan IDs' };
    const catRef = doc(db, "competencias", competenciaId, "categorias", categoriaId);
    const categoriasColRef = collection(db, "competencias", competenciaId, "categorias");

    // Crear copia de los datos a actualizar para no modificar el original
    const dataToUpdate = { ...newData };
    // Evitar actualizar el ID si accidentalmente viene en newData
    delete dataToUpdate.id;

    try {
      // Validación ANTES de actualizar si cambian campos únicos (código, catNumber)
      const currentDocSnap = await getDoc(catRef);
      if (!currentDocSnap.exists()) {
          return { wasUpdated: false, reason: 'La categoría a actualizar no existe.' };
      }
      const currentData = currentDocSnap.data();

      // Validar duplicado de CÓDIGO si se está cambiando
      if (dataToUpdate.codigo && dataToUpdate.codigo !== currentData.codigo) {
          const q = query(categoriasColRef, where("codigo", "==", dataToUpdate.codigo));
          const snapshot = await getDocs(q);
          if (!snapshot.empty) {
              return { wasUpdated: false, reason: `El código '${dataToUpdate.codigo}' ya existe.` };
          }
      }

      // Validar duplicado de NÚMERO DE CATEGORÍA si se está cambiando
      if (dataToUpdate.catNumber && dataToUpdate.catNumber !== currentData.catNumber) {
          const numToValidate = Number(dataToUpdate.catNumber);
           if (!isNaN(numToValidate) && numToValidate > 0) {
               const q = query(categoriasColRef, where("catNumber", "==", numToValidate));
               const snapshot = await getDocs(q);
               // Asegurarse que el único documento encontrado (si existe) no sea el que estamos actualizando
               if (!snapshot.empty && snapshot.docs[0].id !== categoriaId) {
                    return { wasUpdated: false, reason: `El número de categoría #${numToValidate} ya existe.` };
               }
           } else if (dataToUpdate.hasOwnProperty('catNumber')) { // Si se intenta poner un valor inválido
               return { wasUpdated: false, reason: `El número de categoría debe ser un número positivo.` };
           }
      }

      // Proceder con la actualización si pasa las validaciones
      await updateDoc(catRef, dataToUpdate);

       // Opcional: Actualizar caché local
       setCompetencias(prev => prev.map(comp => {
           if (comp.id === competenciaId) {
               const updatedCategorias = (comp.categorias || []).map(cat =>
                   cat.id === categoriaId ? { ...cat, ...dataToUpdate } : cat
               );
               return { ...comp, categorias: updatedCategorias };
           }
           return comp;
       }));

      console.log(`[CompetenciaContext] Categoría ${categoriaId} actualizada en ${competenciaId}`);
      return { wasUpdated: true, reason: '' };
    } catch (error) {
      console.error("[CompetenciaContext] Error actualizando categoría:", competenciaId, categoriaId, error);
      return { wasUpdated: false, reason: 'Error de Firestore al actualizar categoría', error: error };
    }
  }, []); // Sin dependencias de estado local para la lógica principal

// En CompetenciaContext.js
const deleteCategoria = useCallback(async (competenciaId, categoriaId) => {
    if (!user?.uid) {
      return { wasDeleted: false, reason: 'No autenticado' };
    }
  
    const catRef = doc(db, "competencias", competenciaId, "categorias", categoriaId);
    
    try {
      const catSnap = await getDoc(catRef);
      if (!catSnap.exists()) {
        return { wasDeleted: false, reason: 'La categoría no existe' };
      }
  
      await deleteDoc(catRef);

      await deleteDoc(catRef);

      // 2. Obtener todas las pruebas de la competencia
      const pruebasColRef = collection(db, "competencias", competenciaId, "pruebas");
      const pruebasSnapshot = await getDocs(pruebasColRef);
      
      // 3. Preparar batch para actualizar pruebas
      const batch = writeBatch(db);
      
      pruebasSnapshot.forEach((pruebaDoc) => {
        const pruebaData = pruebaDoc.data();
        const updatedPrueba = {
          categoriaIdsFemenil: arrayRemove(categoriaId),
          categoriaIdsVaronil: arrayRemove(categoriaId)
        };
        
        // Solo actualizar si hay cambios
        if (pruebaData.categoriaIdsFemenil?.includes(categoriaId) || 
            pruebaData.categoriaIdsVaronil?.includes(categoriaId)) {
          batch.update(pruebaDoc.ref, updatedPrueba);
        }
      });
      
          // 4. Ejecutar actualizaciones
    await batch.commit();

      // Actualizar estado local
      setCompetencias(prev => prev.map(comp => {
        if (comp.id === competenciaId) {
        // Actualizar categorías
        const newCategorias = comp.categorias.filter(cat => cat.id !== categoriaId);
        
        // Actualizar pruebas
        const newPruebas = comp.pruebas.map(prueba => ({
          ...prueba,
          categoriaIdsFemenil: prueba.categoriaIdsFemenil?.filter(id => id !== categoriaId) || [],
          categoriaIdsVaronil: prueba.categoriaIdsVaronil?.filter(id => id !== categoriaId) || []
        }));
        
        return { ...comp, categorias: newCategorias, pruebas: newPruebas };
      }
        return comp;
      }));
  
      return { wasDeleted: true, reason: '' };
    } catch (error) {
      console.error("Error eliminando categoría:", error);
      return { 
        wasDeleted: false, 
        reason: error.message || 'Error de Firestore al eliminar categoría' 
      };
    }
  }, []);

  // En tu CompetenciaContext.js
const deleteCategorias = useCallback(async (competenciaId, categoriasIds) => {
    if (!competenciaId || !categoriasIds?.length) {
      return { wasDeleted: false, reason: 'Datos inválidos' };
    }
  
    const batch = writeBatch(db);
    
    try {
      // Verificar permisos primero
      const competenciaRef = doc(db, "competencias", competenciaId);
      const competenciaDoc = await getDoc(competenciaRef);
      
      if (!competenciaDoc.exists()) {
        return { wasDeleted: false, reason: 'La competencia no existe' };
      }
      
      // Añadir todas las eliminaciones al batch
      categoriasIds.forEach(id => {
        const catRef = doc(db, "competencias", competenciaId, "categorias", id);
        batch.delete(catRef);
      });

          // Actualizar pruebas (similar a deleteCategoria pero en batch)
    const pruebasColRef = collection(db, "competencias", competenciaId, "pruebas");
    const pruebasSnapshot = await getDocs(pruebasColRef);
    
    pruebasSnapshot.forEach(pruebaDoc => {
      const pruebaData = pruebaDoc.data();
      const updates = {};
      
      categoriasIds.forEach(catId => {
        if (pruebaData.categoriaIdsFemenil?.includes(catId)) {
          updates.categoriaIdsFemenil = arrayRemove(catId);
        }
        if (pruebaData.categoriaIdsVaronil?.includes(catId)) {
          updates.categoriaIdsVaronil = arrayRemove(catId);
        }
      });
      
      if (Object.keys(updates).length > 0) {
        batch.update(pruebaDoc.ref, updates);
      }
    });

  
      await batch.commit();

      return { wasDeleted: true, reason: '', count: categoriasIds.length };
    } catch (error) {
      console.error("Error eliminando categorías:", error);
      return { 
        wasDeleted: false, 
        reason: error.message || 'Error al eliminar categorías',
        count: 0
      };
    }
  }, []);

  const reorderAndUpdateCategorias = useCallback(async (competenciaId, orderedCategorias) => {
        // Asignar nuevos números basados en el orden
        const withNewNumbers = orderedCategorias.map((cat, index) => ({
          ...cat,
          catNumber: index + 1  // Comenzar desde 1
      }));
    if (!competenciaId || !Array.isArray(orderedCategorias)) {
      console.error("reorderAndUpdateCategorias: Faltan datos necesarios.");
      return { success: false, reason: 'Datos inválidos para reordenar.' };
    }

    const batch = writeBatch(db);
    const categoriasColRef = collection(db, "competencias", competenciaId, "categorias");

    console.log(`[CompetenciaContext] Preparando batch para actualizar catNumber de ${orderedCategorias.length} categorías en ${competenciaId}`);

        // Usar withNewNumbers en lugar de orderedCategorias
        withNewNumbers.forEach((categoria) => {
          if (categoria?.id && typeof categoria.catNumber === 'number') {
              const catRef = doc(categoriasColRef, categoria.id);
              batch.update(catRef, { catNumber: categoria.catNumber });
          } else {
              console.warn("[CompetenciaContext] Omitiendo categoría inválida:", categoria);
          }
      });
  
    orderedCategorias.forEach((categoria) => {
      if (categoria && categoria.id && typeof categoria.catNumber === 'number') {
        const catRef = doc(categoriasColRef, categoria.id);
        // Actualiza solo el campo catNumber
        batch.update(catRef, { catNumber: categoria.catNumber });
      } else {
         console.warn("[CompetenciaContext] Omitiendo categoría inválida en batch de reordenamiento:", categoria);
      }
    });

    try {
      await batch.commit();
      console.log(`[CompetenciaContext] Batch de reordenamiento de catNumber completado para ${competenciaId}`);
      
              // Actualizar estado local con los nuevos números
              setCompetencias(prev => prev.map(comp => {
                if (comp.id === competenciaId) {
                    return {
                        ...comp,
          categorias: withNewNumbers,
          pruebas: comp.pruebas.map(prueba => ({
            ...prueba,
          }))
        };
      }
                return comp;
            }));
      return { success: true };
    } catch (error) {
      console.error("[CompetenciaContext] Error ejecutando batch de reordenamiento de catNumber:", error);
      return { success: false, reason: 'Error de Firestore al actualizar números de categoría.', error: error };
    }
  }, []); // Sin dependencias si solo usa db


  // Esta función ahora simplemente llama a updateCategoria
  const updateFechaCorteCategoria = useCallback(async (competenciaId, categoriaId, nuevaFechaCorte) => {
      // Validar formato de fecha si es necesario
      if (!/^\d{4}-\d{2}-\d{2}$/.test(nuevaFechaCorte)) {
          return { wasUpdated: false, reason: 'Formato de fecha inválido (YYYY-MM-DD)' };
      }
      return updateCategoria(competenciaId, categoriaId, { fechaCorte: nuevaFechaCorte });
  }, [updateCategoria]);

  // **Pruebas** (Lógica similar a Categorías)
  // Función para agregar una prueba a una competencia
  const addPrueba = useCallback(async (competenciaId, prueba) => {
    if (!competenciaId) {
      console.error("No se proporcionó competenciaId");
      return { wasAdded: false, reason: "Falta competenciaId" };
    }

    console.log("Agregando prueba a la competencia:", competenciaId);
    const pruebasColRef = collection(db, "competencias", competenciaId, "pruebas");
    const newPruebaId = uuidv4();

    // Usa valores predefinidos si el eventCode coincide
    const predefined = prueba.eventCode ? predefinedPruebasMap[prueba.eventCode] : null;

    const nuevaPruebaData = {
      id: newPruebaId,
      nombre: predefined?.nombre || prueba.nombre || "",
      descripcion: prueba.descripcion || "",
      categoriaIdsFemenil: prueba.categoriaIdsFemenil || [],
      categoriaIdsVaronil: prueba.categoriaIdsVaronil || [],
      testType: predefined?.testType || prueba.testType || "",
      eventCode: prueba.eventCode || "",
      tipoMarca: predefined?.tipoMarca || prueba.tipoMarca || "",
      createdAt: new Date().toISOString(), // Ejemplo de campo timestamp
      // Otros campos que puedas necesitar
    };

    // Validaciones básicas
    if (!nuevaPruebaData.nombre) {
      return { wasAdded: false, reason: "El nombre de la prueba es obligatorio." };
    }
    if (!nuevaPruebaData.eventCode) {
      console.warn("[CompetenciaContext] Añadiendo prueba sin eventCode:", nuevaPruebaData.nombre);
    }

    // Validar que el eventCode sea único dentro de la competencia (si se estableció)
    if (nuevaPruebaData.eventCode) {
      const q = query(pruebasColRef, where("eventCode", "==", nuevaPruebaData.eventCode));
      const existingSnapshot = await getDocs(q);
      if (!existingSnapshot.empty) {
        return {
          wasAdded: false,
          reason: `El código de evento (eventCode) '${nuevaPruebaData.eventCode}' ya existe en esta competencia.`
        };
      }
    }

    try {
      await setDoc(doc(pruebasColRef, newPruebaId), nuevaPruebaData);

      // Actualizar el estado local (caché)
      setCompetencias((prev) =>
        prev.map((comp) => {
          if (comp.id === competenciaId) {
            return { ...comp, pruebas: [...(comp.pruebas || []), nuevaPruebaData] };
          }
          return comp;
        })
      );

      console.log(`[CompetenciaContext] Prueba ${newPruebaId} ('${nuevaPruebaData.nombre}') añadida a ${competenciaId}`);
      return { wasAdded: true, reason: "", id: newPruebaId };
    } catch (error) {
      console.error("[CompetenciaContext] Error añadiendo prueba:", competenciaId, error);
      return { wasAdded: false, reason: "Error de Firestore al añadir prueba", error: error };
    }
  }, [predefinedPruebasMap]);

  // Función para actualizar una prueba
  const updatePrueba = useCallback(async (competenciaId, pruebaId, updatedPrueba) => {
    if (!competenciaId || !pruebaId) return { wasUpdated: false, reason: "Faltan IDs" };

    const pruebaRef = doc(db, "competencias", competenciaId, "pruebas", pruebaId);
    const pruebasColRef = collection(db, "competencias", competenciaId, "pruebas");

    // Crear una copia sin el id, ya que este no debe actualizarse
    const dataToUpdate = {
      ...updatedPrueba,
      categoriaIdsFemenil: updatedPrueba.categoriaIdsFemenil || [],
      categoriaIdsVaronil: updatedPrueba.categoriaIdsVaronil || []
    };

    // Validaciones básicas
    if (dataToUpdate.hasOwnProperty("nombre") && !dataToUpdate.nombre) {
      return { wasUpdated: false, reason: "El nombre de la prueba no puede estar vacío." };
    }

    try {
      // Si se está cambiando el eventCode, validarlo para que sea único
      if (dataToUpdate.eventCode) {
        const currentDocSnap = await getDoc(pruebaRef);
        if (!currentDocSnap.exists()) return { wasUpdated: false, reason: "La prueba a actualizar no existe." };

        const currentEventCode = currentDocSnap.data().eventCode;
        if (dataToUpdate.eventCode !== currentEventCode) {
          const q = query(pruebasColRef, where("eventCode", "==", dataToUpdate.eventCode));
          const existingSnapshot = await getDocs(q);
          if (!existingSnapshot.empty && existingSnapshot.docs[0].id !== pruebaId) {
            return { wasUpdated: false, reason: `El código de evento (eventCode) '${dataToUpdate.eventCode}' ya existe.` };
          }
          // Si existe un valor predefinido para el nuevo eventCode, actualizar otros campos
          const predefined = predefinedPruebasMap[dataToUpdate.eventCode];
          if (predefined) {
            dataToUpdate.nombre = dataToUpdate.nombre || predefined.nombre;
            dataToUpdate.testType = predefined.testType;
            dataToUpdate.tipoMarca = predefined.tipoMarca || "";
          }
        }
      } else if (dataToUpdate.hasOwnProperty("eventCode") && !dataToUpdate.eventCode) {
        console.warn("[CompetenciaContext] Actualizando prueba para que no tenga eventCode:", pruebaId);
      }

      await updateDoc(pruebaRef, dataToUpdate);

      // Actualizar el estado local (caché)
      setCompetencias((prev) =>
        prev.map((comp) => {
          if (comp.id === competenciaId) {
            const updatedPruebas = (comp.pruebas || []).map((pru) =>
              pru.id === pruebaId ? { ...pru, ...dataToUpdate } : pru
            );
            return { ...comp, pruebas: updatedPruebas };
          }
          return comp;
        })
      );

      console.log(`[CompetenciaContext] Prueba ${pruebaId} actualizada en ${competenciaId}`);
      return { wasUpdated: true, reason: "" };
    } catch (error) {
      console.error("[CompetenciaContext] Error actualizando prueba:", competenciaId, pruebaId, error);
      return { wasUpdated: false, reason: "Error de Firestore al actualizar prueba", error: error };
    }
  }, [predefinedPruebasMap]);

// En el provider, modifica fetchPruebas:
const fetchPruebas = useCallback(async (competenciaId) => {
  try {
    const pruebasColRef = collection(db, "competencias", competenciaId, "pruebas");
    const snapshot = await getDocs(pruebasColRef);
    return snapshot.docs.map(doc => {
      const d = doc.data();
      return {
        id: doc.id,
        nombre: d?.nombre ?? '',
        descripcion: typeof d?.descripcion === 'string' ? d.descripcion : '',
        categoriaIdsFemenil: Array.isArray(d?.categoriaIdsFemenil) ? d.categoriaIdsFemenil : [],
        categoriaIdsVaronil: Array.isArray(d?.categoriaIdsVaronil) ? d.categoriaIdsVaronil : [],
        testType: d?.testType ?? '',
        eventCode: d?.eventCode ?? '',
        tipoMarca: d?.tipoMarca ?? '',
        // 👇 importante
        notesByCategoria: (d && typeof d.notesByCategoria === 'object') ? d.notesByCategoria : {},
      };
    });
  } catch (error) {
    console.error("Error obteniendo pruebas:", error);
    return [];
  }
}, []);

  // Función para eliminar una prueba
  const deletePrueba = useCallback(async (competenciaId, pruebaId) => {
    if (!competenciaId || !pruebaId) return { wasDeleted: false, reason: "Faltan IDs" };

    const pruebaRef = doc(db, "competencias", competenciaId, "pruebas", pruebaId);
    console.warn(
      `[CompetenciaContext] Intentando borrar prueba ${pruebaId} de ${competenciaId}. Las referencias en inscripciones NO se limpiarán automáticamente en Firestore.`
    );

    try {
      await deleteDoc(pruebaRef);

      // Actualizar el estado local (caché)
      setCompetencias((prev) =>
        prev.map((comp) => {
          if (comp.id === competenciaId) {
            return { ...comp, pruebas: (comp.pruebas || []).filter((pru) => pru.id !== pruebaId) };
          }
          return comp;
        })
      );

      console.log(`[CompetenciaContext] Prueba ${pruebaId} eliminada de ${competenciaId}`);
      return { wasDeleted: true, reason: "" };
    } catch (error) {
      console.error("[CompetenciaContext] Error eliminando prueba:", competenciaId, pruebaId, error);
      return { wasDeleted: false, reason: "Error de Firestore al eliminar prueba", error: error };
    }
  }, []);


  // **Sessions (Jornadas/Eventos)**
  const addSession = useCallback(async (competenciaId, newSession) => {
    // ... (sin cambios significativos) ...
     if (!competenciaId) return { success: false, error: new Error('Falta competenciaId') };
      const sessionsColRef = collection(db, "competencias", competenciaId, "sessions");
      const newSessionId = newSession.id || uuidv4(); // Usar ID proporcionado o generar uno
      // Asegurar estructura mínima
      const sessionData = {
          id: newSessionId,
          nombre: newSession.nombre || `Jornada ${new Date().toLocaleDateString()}`, // Nombre por defecto
          fecha: newSession.fecha || new Date().toISOString().split('T')[0], // Fecha por defecto
          horaInicio: newSession.horaInicio || '08:00', // Hora por defecto
          horaFin: newSession.horaFin || '', // Opcional
          descripcion: newSession.descripcion || '',
          pruebasIds: Array.isArray(newSession.pruebasIds) ? newSession.pruebasIds : [], // IDs de pruebas en esta sesión
          // Otros campos que necesites: lugar específico, etc.
      };
      console.log(`[CompetenciaContext] Intentando añadir session a ${competenciaId}:`, sessionData);
      try {
          await setDoc(doc(sessionsColRef, newSessionId), sessionData);

           // Opcional: Actualizar caché local
           setCompetencias(prev => prev.map(comp => {
               if (comp.id === competenciaId) {
                    return { ...comp, sessions: [...(comp.sessions || []), sessionData] };
               }
               return comp;
            }));

           console.log(`[CompetenciaContext] Session ${newSessionId} añadida a ${competenciaId}`);
           return { success: true, id: newSessionId };
      } catch (error) {
           console.error("[CompetenciaContext] Error añadiendo session:", competenciaId, error);
           return { success: false, error: error };
      }
  }, []); // Sin dependencias de estado

   const updateSession = useCallback(async (competenciaId, updatedSession) => {
    // ... (sin cambios significativos) ...
    if (!competenciaId || !updatedSession?.id) return { success: false, error: new Error('Faltan IDs (competenciaId o sessionId)') };
      const sessionId = updatedSession.id;
      const sessionRef = doc(db, "competencias", competenciaId, "sessions", sessionId);
      // Crear copia sin el id
      const dataToUpdate = { ...updatedSession };
      delete dataToUpdate.id;

      // Validaciones básicas
      if (dataToUpdate.hasOwnProperty('nombre') && !dataToUpdate.nombre) return { success: false, error: new Error('El nombre de la sesión no puede estar vacío') };
      if (dataToUpdate.hasOwnProperty('fecha') && !/^\d{4}-\d{2}-\d{2}$/.test(dataToUpdate.fecha)) return { success: false, error: new Error('Formato de fecha inválido (YYYY-MM-DD)') };
       if (dataToUpdate.hasOwnProperty('horaInicio') && !/^\d{2}:\d{2}$/.test(dataToUpdate.horaInicio)) return { success: false, error: new Error('Formato de hora inválido (HH:MM)') };


      console.log(`[CompetenciaContext] Intentando actualizar session ${sessionId} en ${competenciaId}:`, dataToUpdate);
      try {
           await updateDoc(sessionRef, dataToUpdate);

           // Opcional: Actualizar caché local
           setCompetencias(prev => prev.map(comp => {
               if (comp.id === competenciaId) {
                    const updatedSessions = (comp.sessions || []).map(sess =>
                        sess.id === sessionId ? { ...sess, ...dataToUpdate, id: sessionId } : sess // Asegurar que el id se mantenga
                    );
                   return { ...comp, sessions: updatedSessions };
               }
               return comp;
            }));

           console.log(`[CompetenciaContext] Session ${sessionId} actualizada`);
           return { success: true };
      } catch (error) {
           console.error("[CompetenciaContext] Error actualizando session:", competenciaId, sessionId, error);
           return { success: false, error: error };
      }
  }, []); // Sin dependencias de estado

   const deleteSession = useCallback(async (competenciaId, sessionId) => {
    // ... (sin cambios significativos) ...
    if (!competenciaId || !sessionId) return { success: false, error: new Error('Faltan IDs') };
       const sessionRef = doc(db, "competencias", competenciaId, "sessions", sessionId);
       console.log(`[CompetenciaContext] Intentando eliminar session ${sessionId} de ${competenciaId}`);
       try {
           await deleteDoc(sessionRef);

           // Opcional: Actualizar caché local
            setCompetencias(prev => prev.map(comp => {
                if (comp.id === competenciaId) {
                    return { ...comp, sessions: (comp.sessions || []).filter(s => s.id !== sessionId) };
                }
                return comp;
            }));

            console.log(`[CompetenciaContext] Session ${sessionId} eliminada`);
            return { success: true };
       } catch (error) {
            console.error("[CompetenciaContext] Error eliminando session:", competenciaId, sessionId, error);
           return { success: false, error: error };
       }
   }, []); // Sin dependencias de estado


  // --- Funciones de Opciones Avanzadas (Actualizan doc principal) ---
  // Estas ya estaban usando _updateCompetenciaDoc correctamente
  const updateOpcionesAvanzadas = useCallback(async (competenciaId, opcionesAvanzadas) => {
      // Se llama desde updateAdvancedRules
      console.warn("[CompetenciaContext] updateOpcionesAvanzadas es obsoleta, usar updateAdvancedRules");
      return updateAdvancedRules(competenciaId, opcionesAvanzadas);
  }, [updateAdvancedRules]);

  const updateOpcionesRelevos = useCallback(async (competenciaId, opcionesRelevos) => {
      // Se llama desde updateRelayOptions
      console.warn("[CompetenciaContext] updateOpcionesRelevos es obsoleta, usar updateRelayOptions");
      return updateRelayOptions(competenciaId, opcionesRelevos);
   }, [updateRelayOptions]);


  // --- Importación / Exportación CSV ---
  // Exportación usa el caché local (advertencia sobre posible desactualización)
  // Importación escribe directamente en Firestore usando batches y valida contra Firestore

  const exportPruebasToCSV = useCallback((competenciaId, filename) => {
    // ¡Advertencia! Usa el caché local. Si el caché no está sincronizado, la exportación puede ser incorrecta.
    console.warn("[CompetenciaContext] Exportando pruebas basado en caché local. Podría no estar sincronizado con Firestore si hubo cambios externos.");
    const competencia = competencias.find((comp) => comp.id === competenciaId);
    if (!competencia) {
        console.error(`[CompetenciaContext] Exportación CSV: Competencia ${competenciaId} no encontrada en caché.`);
        // Podrías mostrar un error al usuario aquí
        return;
    }
    // Usar datos del caché local
    const pruebas = competencia.pruebas || [];
    const categorias = competencia.categorias || [];

    if (pruebas.length === 0) {
      console.warn(`[CompetenciaContext] No hay pruebas en caché para exportar para la competencia ${competenciaId}.`);
      // Informar al usuario que no hay nada que exportar
      alert("No hay pruebas definidas para esta competencia.");
      return;
    }

    // Mapear IDs de categoría a #Cat usando el caché local de categorías
    const categoriasMapById = categorias.reduce((map, cat) => { map[cat.id] = { catNumber: cat.catNumber, codigo: cat.codigo }; return map; }, {});

    const data = pruebas.map((pru) => ({
        eventCode: pru.eventCode || '',
        // Mapear IDs a #Cat. Si un ID no se encuentra en el caché, se pondrá vacío.
        '#CatFemenil': (pru.categoriaIdsFemenil || []).map((id) => categoriasMapById[id]?.catNumber ?? `ID_INVALIDO:${id}`).join(','),
        '#CatVaronil': (pru.categoriaIdsVaronil || []).map((id) => categoriasMapById[id]?.catNumber ?? `ID_INVALIDO:${id}`).join(','),
        // Podrías añadir más campos si los necesitas en el CSV, como nombre, tipoMarca, etc.
        // 'nombre': pru.nombre,
        // 'tipoMarca': pru.tipoMarca,
      }));

    try {
        const csv = Papa.unparse(data, {
            quotes: true, // Poner comillas alrededor de los campos
            header: true, // Incluir la fila de cabecera
            delimiter: ",", // Usar coma como delimitador
            newline: "\r\n" // Usar retorno de carro y nueva línea para compatibilidad Windows/Mac
        });

        // Crear y descargar el archivo
        const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' }); // BOM para Excel
        const url = URL.createObjectURL(blob);
        const linkElement = document.createElement('a'); // Renombrado para claridad
        linkElement.href = url;
        const safeFilename = slugify(filename || `${competencia.general?.nombre || 'competencia'}_pruebas`, { replacement: '_', lower: true });
        linkElement.setAttribute('download', `${safeFilename}.csv`);
        document.body.appendChild(linkElement);
        linkElement.click();
        document.body.removeChild(linkElement);
        URL.revokeObjectURL(url); // Liberar memoria
    } catch (error) {
        console.error("[CompetenciaContext] Error generando o descargando CSV de pruebas:", error);
        alert("Ocurrió un error al generar el archivo CSV de pruebas.");
    }
  }, [competencias]); // Depende del caché local

  const importPruebasFromCSV = useCallback(async (competenciaId, file) => {
       if (!competenciaId) return { wasImported: false, errores: [{ message: 'Falta competenciaId' }] };

       // Retorna una Promesa para poder usar async/await donde se llame
       return new Promise(async (resolve) => { // No necesita reject aquí, resolvemos con el estado
           let importResult = { wasImported: false, count: 0, errores: [] };
           try {
               // 1. Cargar datos necesarios de Firestore para validación
               console.log(`[CompetenciaContext] Iniciando importación CSV de pruebas para ${competenciaId}... Leyendo datos necesarios...`);
               const categoriasColRef = collection(db, "competencias", competenciaId, "categorias");
               const pruebasColRef = collection(db, "competencias", competenciaId, "pruebas");
               const [catsSnapshot, pruebasSnapshot] = await Promise.all([
                   getDocs(categoriasColRef),
                   getDocs(pruebasColRef)
               ]);

               // Crear mapas para búsqueda rápida
               const categoriasMapByNum = catsSnapshot.docs.reduce((map, doc) => {
                    const d = doc.data();
                    if (d.catNumber) map[d.catNumber] = { id: doc.id, ...d }; // Mapea #Cat -> {id, data}
                    return map;
                }, {});
               const existingEventCodes = new Set(pruebasSnapshot.docs.map(doc => doc.data().eventCode).filter(Boolean)); // Ignora eventCodes vacíos/nulos existentes

                console.log(`[CompetenciaContext] Datos leídos: ${Object.keys(categoriasMapByNum).length} categorías con #Cat, ${existingEventCodes.size} eventCodes existentes.`);

               // 2. Parsear el archivo CSV
               Papa.parse(file, {
                   header: true,        // La primera fila es la cabecera
                   skipEmptyLines: true, // Saltar filas vacías
                   complete: async (results) => { // Se ejecuta cuando el parseo termina
                       const { data, errors: parseErrors } = results;
                       console.log(`[CompetenciaContext] CSV Parseado: ${data.length} filas de datos.`);

                       if (parseErrors.length > 0) {
                           console.error("[CompetenciaContext] Errores durante el parseo del CSV:", parseErrors);
                           importResult.errores = parseErrors.map(err => ({ fila: err.row + 2, message: err.message, code: err.code }));
                           resolve(importResult); // Resuelve con errores de parseo
                           return;
                       }

                       const batch = writeBatch(db);
                       const pruebasParaEstadoLocal = [];
                       const eventCodesEnArchivo = new Set(); // Para detectar duplicados DENTRO del archivo

                       // 3. Validar cada fila y preparar el batch
                       data.forEach((row, index) => {
                            const rowIndex = index + 2; // Fila real en el archivo (asumiendo header en fila 1)
                            const { eventCode, '#CatFemenil': catFemenilStr, '#CatVaronil': catVaronilStr } = row;
                            const rowErrors = [];
                            let isValid = true;

                            // --- Validaciones ---
                            // Validar eventCode
                            if (!eventCode || typeof eventCode !== 'string' || !eventCode.trim()) {
                                rowErrors.push("La columna 'eventCode' es obligatoria y no puede estar vacía.");
                                isValid = false;
                            } else {
                                const trimmedEventCode = eventCode.trim();
                                if (!predefinedPruebasMap[trimmedEventCode]) {
                                    rowErrors.push(`El 'eventCode' (${trimmedEventCode}) no corresponde a una prueba predefinida válida.`);
                                    isValid = false;
                                }
                                if (existingEventCodes.has(trimmedEventCode)) {
                                    rowErrors.push(`El 'eventCode' (${trimmedEventCode}) ya existe en esta competencia.`);
                                    isValid = false;
                                }
                                if (eventCodesEnArchivo.has(trimmedEventCode)) {
                                     rowErrors.push(`El 'eventCode' (${trimmedEventCode}) está duplicado dentro de este archivo CSV.`);
                                     isValid = false;
                                }
                            }

                            // Parsear y validar números de categoría
                            const parseCatNumbers = (str) => str ? str.split(',').map(n => parseInt(n.trim(), 10)).filter(n => !isNaN(n)) : [];
                            const catFNums = parseCatNumbers(catFemenilStr);
                            const catVNums = parseCatNumbers(catVaronilStr);
                            const catIdsFem = [];
                            const catIdsVar = [];

                            const mapNumToId = (num, rama) => {
                                const catData = categoriasMapByNum[num];
                                if (!catData) {
                                    rowErrors.push(`El número de categoría #${num} (rama ${rama}) no fue encontrado en la configuración de categorías de esta competencia.`);
                                    isValid = false;
                                    return null;
                                }
                                return catData.id; // Devuelve el ID de Firestore
                            };

                            catFNums.forEach(num => { const id = mapNumToId(num, 'Femenil'); if (id) catIdsFem.push(id); });
                            catVNums.forEach(num => { const id = mapNumToId(num, 'Varonil'); if (id) catIdsVar.push(id); });

                            // Validar que haya al menos una categoría válida asignada
                            if (isValid && catIdsFem.length === 0 && catIdsVar.length === 0) {
                                rowErrors.push("La prueba debe estar asignada al menos a un número de categoría válido (#CatFemenil o #CatVaronil).");
                                isValid = false;
                            }
                            // --- Fin Validaciones ---

                            if (!isValid) {
                                // Si hay errores, añadirlos al resultado general
                                importResult.errores.push({ fila: rowIndex, data: row, errores: rowErrors });
                            } else {
                                // Si es válido, añadir al batch y al set de eventCodes del archivo
                                const trimmedEventCode = eventCode.trim();
                                const predefined = predefinedPruebasMap[trimmedEventCode]; // Ya sabemos que existe por validación previa
                                const newId = uuidv4();
                                const pruebaData = {
                                    id: newId,
                                    nombre: predefined.nombre,
                                    testType: predefined.testType,
                                    eventCode: trimmedEventCode,
                                    categoriaIdsFemenil: catIdsFem,
                                    categoriaIdsVaronil: catIdsVar,
                                    descripcion: '', // O tomarla de 'predefined' si existe y la quieres
                                    tipoMarca: predefined.tipoMarca || '',
                                };
                                batch.set(doc(pruebasColRef, newId), pruebaData);
                                pruebasParaEstadoLocal.push(pruebaData); // Para actualizar caché local si todo va bien
                                eventCodesEnArchivo.add(trimmedEventCode); // Marcar como usado en este archivo
                            }
                       }); // Fin forEach row

                       // 4. Ejecutar Batch si no hubo errores de validación graves
                       if (importResult.errores.length === 0 && pruebasParaEstadoLocal.length > 0) {
                           try {
                               await batch.commit();
                               console.log(`[CompetenciaContext] ${pruebasParaEstadoLocal.length} pruebas importadas exitosamente a ${competenciaId} mediante batch.`);
                               importResult.wasImported = true;
                               importResult.count = pruebasParaEstadoLocal.length;

                               // Opcional: Actualizar caché local (añadir las nuevas pruebas)
                               setCompetencias(prev => prev.map(comp => {
                                   if (comp.id === competenciaId) {
                                       const currentPruebas = Array.isArray(comp.pruebas) ? comp.pruebas : [];
                                       return { ...comp, pruebas: [...currentPruebas, ...pruebasParaEstadoLocal] };
                                   }
                                   return comp;
                               }));

                           } catch (batchError) {
                               console.error("[CompetenciaContext] Error al ejecutar el batch de importación de pruebas:", batchError);
                               importResult.errores.push({ message: "Error de Firestore al guardar las pruebas importadas.", details: batchError.message });
                               importResult.wasImported = false; // Marcar como fallido
                               importResult.count = 0;
                           }
                       } else if (pruebasParaEstadoLocal.length === 0 && importResult.errores.length === 0) {
                            // Caso: archivo vacío o sin filas válidas, pero sin errores directos.
                           console.log("[CompetenciaContext] El archivo CSV no contenía pruebas válidas para importar.");
                           // No se considera un error necesariamente, pero no se importó nada.
                       } else {
                            // Caso: Hubo errores de validación en las filas. El batch no se ejecutó.
                           console.warn(`[CompetenciaContext] La importación de pruebas no se completó debido a ${importResult.errores.length} errores de validación en el archivo.`);
                       }

                       // 5. Resolver la promesa con el resultado final
                       resolve(importResult);

                   }, // Fin complete callback
                   error: (err) => { // Se ejecuta si PapaParse tiene un error crítico al leer/parsear
                       console.error("[CompetenciaContext] Error crítico de PapaParse al leer el CSV de pruebas:", err);
                       importResult.errores.push({ message: "Error crítico al leer el archivo CSV.", details: err.message });
                       resolve(importResult); // Resuelve con el error
                   }
               }); // Fin Papa.parse

           } catch (fetchError) { // Error al leer datos de Firestore ANTES de parsear
               console.error("[CompetenciaContext] Error preparando la importación CSV de pruebas (leyendo datos existentes):", fetchError);
               importResult.errores.push({ message: "Error interno al preparar la importación (acceso a Firestore).", details: fetchError.message });
               resolve(importResult); // Resuelve con el error
           }
       }); // Fin Promise
  }, [predefinedPruebasMap]); // Dependencia del mapa

  const exportCategoriasToCSV = useCallback(async (competenciaId, filename) => {
       // ¡Advertencia! Usa el caché local.
       console.warn("[CompetenciaContext] Exportando categorías basado en caché local. Podría no estar sincronizado.");
       const competenciaLocal = competencias.find(c => c.id === competenciaId);
        if (!competenciaLocal) {
            console.error(`[CompetenciaContext] Exportación CSV: Competencia ${competenciaId} no encontrada en caché.`);
            alert("Competencia no encontrada para exportar categorías.");
            return;
        }
        const categorias = competenciaLocal.categorias || [];
        if (categorias.length === 0) {
            console.warn(`[CompetenciaContext] No hay categorías en caché para exportar para ${competenciaId}.`);
            alert("No hay categorías definidas para esta competencia.");
            return;
        }

        // Mapear los datos al formato del CSV esperado por la importación
        const data = categorias.map(({ catNumber, codigo, nombre, edadMin, edadMax, fechaCorte }) => ({
            '#Cat': catNumber ?? '', // Usar '#Cat' como cabecera
            codigo: codigo ?? '',
            nombre: nombre ?? '',
            edadMin: edadMin ?? '',
            edadMax: edadMax ?? '',
            fechaCorte: fechaCorte ?? '', // Formato YYYY-MM-DD
        }));

        try {
            const csv = Papa.unparse(data, { quotes: true, header: true, delimiter: ",", newline: "\r\n" });
            const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const linkElement = document.createElement('a');
            const safeFilename = slugify(filename || `${competenciaLocal.general?.nombre || 'competencia'}_categorias`, { replacement: '_', lower: true });
            linkElement.setAttribute('download', `${safeFilename}.csv`);
            document.body.appendChild(linkElement);
            linkElement.click();
            document.body.removeChild(linkElement);
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error("[CompetenciaContext] Error generando o descargando CSV de categorías:", error);
            alert("Ocurrió un error al generar el archivo CSV de categorías.");
        }
  }, [competencias]); // Depende del caché local

  const importCategoriasFromCSV = useCallback(async (competenciaId, file) => {
      if (!competenciaId) return { wasImported: false, errores: [{ message: 'Falta competenciaId' }] };

      // Retorna una Promesa
      return new Promise((resolve) => {
         Papa.parse(file, {
             header: true,        // Usar cabeceras
             skipEmptyLines: true, // Saltar filas vacías
             complete: async (results) => { // Callback cuando termina el parseo
                 const { data, errors: parseErrors } = results;

                 if (parseErrors.length > 0) {
                     console.error("[CompetenciaContext] Errores durante el parseo del CSV de categorías:", parseErrors);
                     const formattedErrors = parseErrors.map(err => ({ fila: err.row + 2, message: err.message, code: err.code }));
                     resolve({ wasImported: false, count: 0, errores: formattedErrors });
                     return;
                 }

                 if (data.length === 0) {
                    console.log("[CompetenciaContext] El archivo CSV de categorías está vacío o no contiene datos.");
                     resolve({ wasImported: false, count: 0, errores: [{message: "El archivo CSV está vacío o no contiene filas de datos."}] });
                    return;
                 }

                 console.log(`[CompetenciaContext] CSV de categorías parseado, ${data.length} filas encontradas. Enviando a addCategorias...`);

                 // Llama a la función addCategorias (que ya maneja validación y batch contra Firestore)
                 const result = await addCategorias(competenciaId, data); // addCategorias ahora devuelve { wasAdded, reason, errors?, count? }

                 // Mapea el resultado de addCategorias al formato esperado por la importación
                 resolve({
                     wasImported: result.wasAdded,
                     // Usar el count devuelto por addCategorias si existe, si no, una aproximación
                     count: result.count ?? (result.wasAdded ? data.length : 0),
                     // Usar los errores detallados si existen, si no, el 'reason' general
                     errores: result.errors ?? (result.reason ? [{ message: result.reason }] : []),
                 });
             },
             error: (err) => { // Error crítico de PapaParse
                 console.error("[CompetenciaContext] Error crítico de PapaParse al leer CSV de categorías:", err);
                  resolve({ wasImported: false, count: 0, errores: [{ message: "Error crítico al leer el archivo CSV.", details: err.message }] });
             }
         }); // Fin Papa.parse
      }); // Fin Promise
  }, [addCategorias]); // Depende de addCategorias

  const bumpInscritosLocal = React.useCallback((competenciaId, delta) => {
    setCompetencias((prev) => prev.map((c) => {
      if (c.id !== competenciaId) return c;
      const current = typeof c.inscritos === 'number' ? c.inscritos : 0;
      return { ...c, inscritos: Math.max(0, current + (Number(delta) || 0)) };
    }));
  }, []);
 // --- Valor del Contexto ---
  // Exportar todas las funciones relevantes
  return (
    <CompetenciaContext.Provider
      value={{
        competencias, loading,
        // CRUD Principal
        addCompetencia,
        setCompetencia,
        // updateCompetencia: _updateCompetenciaDoc, // NO exponer la base directamente usualmente
        deleteCompetencia,
        // Actualizaciones Específicas por Sección
        updateGeneralInfo,
        updateImagenPortada,
        updateLogo,
        updateConvocatoria, // Añadido
        updateLocationSettings,
        updateContactInfo,
        updateSocialMedia,
        updateTheme,
        updateAdvancedRules, // Reemplaza updateOpcionesAvanzadas
        updateRelayOptions,  // Reemplaza updateOpcionesRelevos
        updateRegistrationConfig,
        // Valores Default (útiles en formularios)
        defaultRegistroFields,
        defaultRegistroFieldLabels,
        defaultCompetenciaFirestoreFields, // Exportar defaults completos si son útiles externamente
        // CRUD Subcolecciones
        getCategorias: fetchCategorias, // Nueva función
        addCategoria, addCategorias, updateCategoria, reorderAndUpdateCategorias, deleteCategoria, updateFechaCorteCategoria, deleteCategorias,
        fetchPruebas,
        addPrueba, updatePrueba, deletePrueba, // updatePrueba reemplaza updatePruebaEnhanced
        // Inscripciones (carga optimizada)
        bumpInscritosLocal,
        // TODO: Añadir funciones CRUD para Inscripciones si se necesitan (updateInscripcion, deleteInscripcion)
        addSession, updateSession, deleteSession,
        // Import/Export
        exportPruebasToCSV, importPruebasFromCSV, exportCategoriasToCSV, importCategoriasFromCSV,
      }}
    >
      {children}
    </CompetenciaContext.Provider>
      );
    };
    
    CompetenciaProvider.propTypes = {
      children: PropTypes.node.isRequired,
    };
    
    export default CompetenciaProvider;
